import { appendQueryParams, seeMoreItemToCreatePlayable, SeeMoreItem, useUniqueArray, CreateCardCarouselItem } from '@bpm-web-app/utils';
import { Fragment, ReactNode, useMemo } from 'react';
import { useCreateLike } from '@bpm-web-app/swr-hooks';
import classNames from 'classnames';
import { useCreatePlayer } from '../../../../utils/src/lib/create-player.context';
import CardGrid from '../shared/card-grid/card-grid';
import CardCreate, { CreateCardContentTypeWithoutActions } from '../shared/card/card-create';
import { CreateListGridItem } from '../shared/list-grid-item/create-list-grid-item';
import ListGrid from '../shared/ui/list-grid/list-grid';
import BreakpointView from '../shared/ui/breakpoint-view/breakpoint-view';
import SecondaryPageTitle from '../shared/secondary-page-title/secondary-page-title';

import styles from './see-more-layout.module.css';
import { CreateActionType } from '../shared/three-dots-sheet/create-three-dots-sheet.context';
import CardGhostLoading from '../shared/card/card-ghost-loading';
import GhostElement from '../shared/ghost-element/ghost-element';

type BaseProps = {
    count?: number;
    pageTitle?: string;
    contentType: CreateActionType | CreateCardContentTypeWithoutActions;
    loading?: boolean
};

type SeeMoreLayoutNoChildrenProps = BaseProps & {
    itemBasePath?: string;
    data: SeeMoreItem[];
    desktopChildren?: never;
    mobileChildren?: never;
};

type SeeMoreLayoutChildrenProps = BaseProps & {
    itemBasePath?: never;
    data?: never;
    desktopChildren: ReactNode;
    mobileChildren: ReactNode;
};

type SeeMoreLayoutProps = SeeMoreLayoutNoChildrenProps | SeeMoreLayoutChildrenProps;

/** Use this component only on Create App */
export function SeeMoreLayout({ contentType, itemBasePath, data, pageTitle, count = undefined, mobileChildren, desktopChildren, loading }: SeeMoreLayoutProps) {
    const { playDemo } = useCreatePlayer();

    const likeType = useMemo(() => {
        switch (contentType) {
            case 'pack':
                return 'soundPackage';
            case 'curated-set':
                return 'curated';
            case 'sound':
                return 'sound';
            case 'label':
                return 'label';
            case 'contest':
            case 'news':
                return undefined;
            default:
                return 'label';
        }
    }, [contentType]);

    const { isLiked, likeDislike } = useCreateLike(likeType);

    const ghostLoadingItems = useUniqueArray(10);

    if (mobileChildren && desktopChildren) {
        return (
            <article className="spacing--top">
                {pageTitle ? (
                    <div className={styles['see-more-layout__title']}>
                        <SecondaryPageTitle title={pageTitle} counter={`${count ?? ''}`} />
                    </div>
                ) : null}
                <BreakpointView desktopChildren={<div className={classNames('spacing--horizontal', 'spacing--top')}>{desktopChildren}</div>} mobileChildren={mobileChildren} />
            </article>
        );
    }

    /* TODO: consider adding ghost loading to this layout */
    return (
        <article className="spacing__window">
            {pageTitle ? (
                <SecondaryPageTitle title={pageTitle} counter={`${count ?? ''}`} noPadding />
            ) : null}
            <div className={styles['see-more-layout__mobile']}>
                <ListGrid>
                    {loading && ghostLoadingItems.map((uuid) => (
                        <GhostElement
                            isRow
                            key={`see-more-layout-${uuid}`}
                            linesWidthArray={[90, 70]}
                            squareWidth={56}
                            itemClass={styles['see-more-layout__mobile-loading-item']}
                        />
                    ))}
                    {!loading && data.map((item) => (
                        <Fragment key={item.id}>
                            <CreateListGridItem
                                id={item.id}
                                title={item.title}
                                imageUrl={appendQueryParams(item.image_url, { key: 'dw', value: 56 })}
                                imageUrl2x={appendQueryParams(item.image_url, { key: 'dw', value: 112 })}
                                subtitle={item.description ?? item.label}
                                link={itemBasePath ? `${itemBasePath}/${item.slug}` : undefined}
                                contentType={contentType}
                            />
                        </Fragment>
                    ))}
                </ListGrid>
            </div>
            <div className={styles['see-more-layout__desktop']}>
                <CardGrid cardType="card-small">
                    {loading && ghostLoadingItems.map((uuid) => <CardGhostLoading key={`card-ghost-${uuid}`} cardSize="small" linesWidthArray={[90, 70]} lineHeight={10} />)}
                    {!loading && data.map((item) => (
                        <CardCreate
                            hasExtraDemo={!!item.demo_file_url_2}
                            contentType={contentType}
                            key={item.id}
                            id={`${item.id}`}
                            title={item.title}
                            description={item.description}
                            imageUrl={appendQueryParams(item.image_url, { key: 'dw', value: 264 })}
                            imageUrl2x={appendQueryParams(item.image_url, { key: 'dw', value: 528 })}
                            link={`${itemBasePath}/${item.slug}`}
                            cardSize="small"
                            label={item.label}
                            isFavorite={isLiked(String(item.id))}
                            onFavorite={likeDislike}
                            onCardPlay={contentType === 'pack' && item.demo_file_url ? (id) => playDemo(seeMoreItemToCreatePlayable(data.find((packItem) => packItem.id === id))) : undefined}
                            shareURL={item.shareUrl}
                            badge={item.badge}
                            items={data as unknown as CreateCardCarouselItem[]}
                        />
                    ))}
                </CardGrid>
            </div>
        </article>
    );
}

export default SeeMoreLayout;
